/**
 * What a supplier contact was ABOUT — one vocabulary, two readers.
 *
 * WHY THIS FILE EXISTS. The reasons offered used to be the agreement STATUS
 * vocabulary (Jeff, 2026-09-03: "you can use the different statuses that we
 * have now"), and that turned out to be the wrong axis. Stacie, on staging
 * 2026-09-09, could not answer the question the form was asking:
 *
 *   "The what was it about? I can't answer that question… RFP sent, chasing a
 *    response, negotiating, accepted, arranging the loading."
 *   "that's already in there though. So this is more when I call, what am I
 *    doing?"
 *
 * The status is already on the agreement. Asking for it again on the call
 * records the same fact twice and asks the caller to translate their own
 * purpose into workflow terms. The reason is the PURPOSE of the call — Jeff's
 * reframing, which she took: "the purpose of the call was RFP related, rate
 * loading related, renewal related, or other?" — "or rate audit related. Yep…
 * What was the conversation about? Yeah, I like that."
 *
 * `general` stays because she asked for it twice: "Sometimes it's just a hotel
 * calling to call."
 *
 * NO MIGRATION, AND NO API CHANGE. `reason` is free text on the API on purpose
 * — a closed list in that parser "would 400 a legitimate reason every time the
 * vocabulary moves", which is exactly what moving it proves. The cost is that
 * rows written before today hold the retired values, and the history table
 * renders `reason` directly. Hence `LEGACY_LABELS`: the old vocabulary is
 * READ but never OFFERED.
 */

export interface ContactReason {
  value: string;
  /** Shown in the picker, where there is room to disambiguate. */
  label: string;
  /**
   * Shown in the history table, where there is not. Only differs where the
   * picker label carries a clarifying tail the column cannot fit.
   */
  short?: string;
}

export const CONTACT_REASONS: readonly ContactReason[] = [
  {
    value: 'general',
    label: 'General — no specific agreement',
    short: 'General',
  },
  /*
   * THE DISCOVERY CALL, added 2026-09-11 from Stacie's answer about where an
   * initial conversation lives:
   *
   *   "a client requests / or we see a potential opportunity for an RFP, that
   *    should put it in the sourcing status. the first step of sourcing is to
   *    contact the hotel for an initial conversation to see if the partnership
   *    is truly an opportunity worth pursuing. if so, the status would then
   *    progress to sending the RFP. if not, it would move to dismissed"
   *
   * So it is NOT a status — the agreement is already in sourcing when the call
   * happens — and it is not the same purpose as `rfp` either: this call decides
   * whether there will BE an RFP, and its other outcome is a dismissal. Logging
   * it as "RFP" would lose exactly the distinction she drew, and with it the
   * ability to ask how many opportunities die at first contact.
   */
  {
    value: 'initial_conversation',
    label: 'Initial conversation — is this worth pursuing?',
    short: 'Initial conversation',
  },
  { value: 'rfp', label: 'RFP' },
  { value: 'rate_loading', label: 'Rate loading' },
  { value: 'rate_audit', label: 'Rate audit' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'other', label: 'Other' },
];

/**
 * The reasons that name NO particular agreement.
 *
 * This used to be derived as "every reason except `general`", which was true
 * while `general` was the only catch-all. Adding `other` breaks that: read as a
 * negation, `other` would start DEMANDING an agreement, so a miscellaneous call
 * — the exact thing `other` is for — could not be logged without attaching one.
 * Stated positively so a future addition has to declare which side it is on.
 */
export const REASONS_WITHOUT_AN_AGREEMENT: ReadonlySet<string> = new Set([
  'general',
  'other',
]);

/** Reasons that DO name an agreement, so the form should offer the picker. */
export const REASONS_WANTING_AN_AGREEMENT: ReadonlySet<string> = new Set(
  CONTACT_REASONS.map((r) => r.value).filter(
    (v) => !REASONS_WITHOUT_AN_AGREEMENT.has(v),
  ),
);

/**
 * The retired status-shaped vocabulary, for HISTORY ONLY.
 *
 * `loading` and `renewal` mean exactly what `rate_loading` and `renewal` mean
 * now, so they read as the current label — the person's intent did not change
 * when the value did. The rest were statuses, and are named as the statuses
 * they were rather than being force-fitted onto a purpose they never carried.
 */
const LEGACY_LABELS: Readonly<Record<string, string>> = {
  loading: 'Rate loading',
  rfp_sent: 'RFP sent',
  awaiting_response: 'Chasing a response',
  negotiating: 'Negotiating',
  accepted: 'Accepted — arranging the loading',
  active: 'Live rate — an issue or a question',
};

/**
 * Human label for a stored reason, for the history table.
 *
 * Falls through current → retired → humanised, and never returns the raw
 * value: an unrecognised reason is still someone's record of a real call, and
 * `some_new_value` in the About column reads as a bug in the log rather than as
 * a reason nobody has taught this file about yet.
 */
export function contactReasonLabel(reason: string): string {
  const current = CONTACT_REASONS.find((r) => r.value === reason);
  if (current) return current.short ?? current.label;
  const legacy = LEGACY_LABELS[reason];
  if (legacy) return legacy;
  const spaced = reason.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : reason;
}
